/**
 * PATH: apps/qa/src/components/SortableFloorItem.tsx
 * PURPOSE: Sortable floor item for the inspection structure drawer.
 * WHY: Extracted from InspectionStructureDrawer.tsx to keep files under 300 lines.
 */

import { useState, useRef, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { floorApi, areaApi } from '@/lib/api'
import type { FloorItem } from '@/lib/api'
import { cn } from '@/lib/utils'
import {
  Plus, GripVertical, ChevronDown, ChevronRight,
  Pencil, Trash2, Check, XCircle, Loader2,
  AlertTriangle, Copy,
} from 'lucide-react'
import { AreaList } from './SortableAreaItem'

export function SortableFloorItem({
  floor,
  inspectionId,
  expandedFloors,
  toggleFloor,
  onScrollTo,
  onApplyTemplate,
}: {
  floor: FloorItem
  inspectionId: string
  expandedFloors: Set<string>
  toggleFloor: (id: string) => void
  onScrollTo: (type: 'floor' | 'area', id: string) => void
  onApplyTemplate?: (areaIds: string[]) => void
}) {
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState(floor.floor_name)
  const [isAddingArea, setIsAddingArea] = useState(false)
  const [newAreaName, setNewAreaName] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const editRef = useRef<HTMLInputElement>(null)
  const newAreaRef = useRef<HTMLInputElement>(null)
  const isExpanded = expandedFloors.has(floor.id)

  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: floor.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.8 : 1,
  }

  const renameMutation = useMutation({
    mutationFn: (name: string) => floorApi.updateFloor(floor.id, { floor_name: name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-floors', inspectionId] })
      setIsEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => floorApi.deleteFloor(floor.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-floors', inspectionId] })
      queryClient.invalidateQueries({ queryKey: ['qa-inspection', inspectionId] })
    },
  })

  const cloneMutation = useMutation({
    mutationFn: () => floorApi.cloneFloor(floor.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-floors', inspectionId] })
    },
  })

  const addAreaMutation = useMutation({
    mutationFn: (name: string) =>
      areaApi.createArea({
        floor: floor.id,
        area_name: name,
        sort_order: floor.areas?.length || 0,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-floors', inspectionId] })
      setNewAreaName('')
      setIsAddingArea(false)
    },
  })

  useEffect(() => { if (isEditing) editRef.current?.focus() }, [isEditing])
  useEffect(() => { if (isAddingArea) newAreaRef.current?.focus() }, [isAddingArea])

  const areas = floor.areas || []
  const areaCount = areas.length
  const lockedCount = areas.filter(a => a.status === 'locked').length

  return (
    <div ref={setNodeRef} style={style} className={cn('border border-border rounded-lg overflow-hidden', isDragging && 'shadow-lg ring-2 ring-primary/30')}>
      {/* Floor header */}
      <div className="flex items-center gap-1 px-2 py-1.5 bg-muted/50 hover:bg-muted/80 transition-colors">
        <button {...attributes} {...listeners} className="p-0.5 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground" title="Drag to reorder">
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => toggleFloor(floor.id)} className="p-0.5 text-muted-foreground hover:text-foreground">
          {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>

        {isEditing ? (
          <form className="flex-1 flex items-center gap-1"
            onSubmit={(e) => { e.preventDefault(); if (editName.trim()) renameMutation.mutate(editName.trim()) }}>
            <input ref={editRef} value={editName} onChange={(e) => setEditName(e.target.value)}
              className="flex-1 text-xs font-medium px-1.5 py-0.5 rounded border border-input bg-background text-foreground focus:ring-1 focus:ring-primary"
              onKeyDown={(e) => { if (e.key === 'Escape') { setIsEditing(false); setEditName(floor.floor_name) } }} />
            <button type="submit" disabled={renameMutation.isPending} className="p-0.5 text-emerald-600 hover:text-emerald-700">
              {renameMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            </button>
            <button type="button" onClick={() => { setIsEditing(false); setEditName(floor.floor_name) }} className="p-0.5 text-muted-foreground hover:text-foreground">
              <XCircle className="w-3 h-3" />
            </button>
          </form>
        ) : (
          <button onClick={() => onScrollTo('floor', floor.id)} className="flex-1 text-left min-w-0 group">
            <span className="text-xs font-semibold text-foreground group-hover:text-primary transition-colors truncate block">
              {floor.floor_name}
            </span>
          </button>
        )}

        {!isEditing && (
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <span className="text-[10px] text-muted-foreground tabular-nums mr-1">{lockedCount}/{areaCount}</span>
            <button onClick={() => { setIsEditing(true); setEditName(floor.floor_name) }} className="p-0.5 text-muted-foreground hover:text-foreground" title="Rename">
              <Pencil className="w-3 h-3" />
            </button>
            <button onClick={() => cloneMutation.mutate()} disabled={cloneMutation.isPending} className="p-0.5 text-muted-foreground hover:text-blue-600" title="Clone floor">
              {cloneMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Copy className="w-3 h-3" />}
            </button>
            <button onClick={() => setDeleteConfirm(true)} className="p-0.5 text-muted-foreground hover:text-destructive" title="Delete floor">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="px-3 py-2 bg-destructive/10 border-t border-destructive/20">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-destructive flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs font-medium text-destructive">Delete "{floor.floor_name}"?</p>
              <p className="text-[10px] text-destructive/70 mt-0.5">This will delete {areaCount} area{areaCount !== 1 ? 's' : ''} and all associated checklist data.</p>
              <div className="flex items-center gap-2 mt-1.5">
                <button onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}
                  className="px-2 py-0.5 text-[10px] font-medium rounded bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
                <button onClick={() => setDeleteConfirm(false)} className="px-2 py-0.5 text-[10px] font-medium rounded border border-input hover:bg-accent">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Areas (when expanded) */}
      {isExpanded && (
        <AreaList floor={floor} areas={areas} inspectionId={inspectionId} onScrollTo={onScrollTo} onApplyTemplate={onApplyTemplate} />
      )}

      {/* Add area */}
      {isExpanded && (
        <div className="border-t border-border/50">
          {isAddingArea ? (
            <form className="flex items-center gap-1.5 px-3 py-1.5"
              onSubmit={(e) => { e.preventDefault(); if (newAreaName.trim()) addAreaMutation.mutate(newAreaName.trim()) }}>
              <input ref={newAreaRef} value={newAreaName} onChange={(e) => setNewAreaName(e.target.value)}
                placeholder="Area name..."
                className="flex-1 text-xs px-2 py-1 rounded border border-input bg-background text-foreground placeholder:text-muted-foreground focus:ring-1 focus:ring-primary"
                onKeyDown={(e) => { if (e.key === 'Escape') { setIsAddingArea(false); setNewAreaName('') } }} />
              <button type="submit" disabled={addAreaMutation.isPending || !newAreaName.trim()} className="p-1 text-emerald-600 hover:text-emerald-700 disabled:opacity-50">
                {addAreaMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              </button>
              <button type="button" onClick={() => { setIsAddingArea(false); setNewAreaName('') }} className="p-1 text-muted-foreground hover:text-foreground">
                <XCircle className="w-3.5 h-3.5" />
              </button>
            </form>
          ) : (
            <button onClick={() => setIsAddingArea(true)}
              className="w-full flex items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground hover:text-primary hover:bg-accent/50 transition-colors">
              <Plus className="w-3 h-3" /> Add area
            </button>
          )}
        </div>
      )}
    </div>
  )
}
